import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  Plus,
  Loader2,
  Search,
  Edit,
  Trash2,
  MoreHorizontal,
  Eye,
  TrendingUp,
  Package,
  Percent,
  AlertTriangle,
  Copy,
  Building2,
  CalendarDays
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useNotifications } from '../store/useStore';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { PriceListDetailPage } from './PriceListDetailPage';
import { PriceListPrintView } from './PriceListPrintView';
import type { Database } from '../types/database.types';
import BulkPriceListModal from '../components/BulkPriceListModal';
import { pickCustomLineFields } from '../lib/priceListCustomLines';
import { cn } from '../lib/cn';

type PriceList = Database['public']['Tables']['price_lists']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];
type PriceListItemRow = Database['public']['Tables']['price_list_items']['Row'];

interface PriceListWithDetails extends PriceList {
  customer?: Customer;
  product_count: number;
  status: 'active' | 'draft' | 'expired';
  creator_name?: string | null;
}

type PriceListWithItems = PriceList & {
  price_list_items: PriceListItemRow[];
};

interface KPIData {
  total: number;
  active: number;
  drafts: number;
  averageDiscount: number;
}

export const PriceListsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [priceLists, setPriceLists] = useState<PriceListWithDetails[]>([]);
  const [kpiData, setKpiData] = useState<KPIData>({
    total: 0,
    active: 0,
    drafts: 0,
    averageDiscount: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCreatorFilter, setSelectedCreatorFilter] = useState<string>('all'); // Cambiato da 'me' a 'all' per mostrare tutti i listini di default
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPriceListId, setSelectedPriceListId] = useState<string | undefined>();
  const [showPrintView, setShowPrintView] = useState(false);
  const [printPriceListId, setPrintPriceListId] = useState<string>('');
  const [priceListToArchive, setPriceListToArchive] = useState<PriceListWithDetails | null>(null); // MODIFICA 1: Rinominato da priceListToDelete
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [priceListToDuplicate, setPriceListToDuplicate] = useState<PriceListWithDetails | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { addNotification } = useNotifications();
  const { user, profile } = useAuth();

  // Apre in modifica un'offerta passata via ?edit=<id> (es. da lead → genera offerta)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    setSelectedPriceListId(editId);
    setShowDetailModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Imposta il filtro iniziale in base al ruolo
  useEffect(() => {
    if (profile?.role && (profile.role === 'commerciale' || profile.role === 'sales')) {
      setSelectedCreatorFilter('me');
    }
  }, [profile?.role]);

  const loadPriceLists = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);

      // Se l'utente non è loggato, non caricare nulla
      if (!user?.id) {
        setPriceLists([]);
        setKpiData({ total: 0, active: 0, drafts: 0, averageDiscount: 0 });
        if (!isBackground) setLoading(false);
        return;
      }

      // Carica TUTTI i listini attivi (non solo quelli dell'utente corrente)
      // Il filtro per utente verrà applicato nel rendering
      const { data: priceListsData, error: priceListsError } = await supabase
        .from('price_lists')
        .select(`
          *,
          price_list_items(count)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (priceListsError) throw priceListsError;

      // Ensure we have valid data
      if (!priceListsData) {
        setPriceLists([]);
        return;
      }

      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('id, company_name')
        .eq('is_active', true);

      if (customersError) throw customersError;

      // Fetch creator profiles - try to get all profiles, fallback to individual queries if RLS blocks
      const creatorIds = [...new Set(priceListsData.map((pl: any) => pl.created_by).filter(Boolean))];
      let profilesData: Array<{ id: string; full_name: string | null; email?: string | null }> = [];
      if (creatorIds.length > 0) {
        try {
          // Try to fetch all creator profiles at once
          // Note: using full_name (singular) as per database schema
          const { data, error: profilesError } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .in('id', creatorIds);

          if (profilesError) {
            // If batch query fails due to RLS, try fetching individually
            // This is a fallback - ideally RLS should allow reading profiles for list creators
            for (const creatorId of creatorIds) {
              try {
                const { data: singleProfile, error: singleError } = await supabase
                  .from('profiles')
                  .select('id, full_name, email')
                  .eq('id', creatorId)
                  .single();
                if (singleProfile) {
                  profilesData.push(singleProfile as { id: string; full_name: string | null; email?: string | null });
                } else if (singleError) {
                  // Skip profiles that can't be accessed
                }
              } catch (err) {
                // Skip profiles that can't be accessed
              }
            }
          } else {
            profilesData = data || [];
          }
        } catch (error) {
          profilesData = [];
        }
      }

      const processedPriceLists: PriceListWithDetails[] = priceListsData.map((priceList: any) => {
        const customer = (customersData as any[])?.find((c: any) => c.id === priceList.customer_id);
        const creator = profilesData.find(p => p.id === priceList.created_by);
        const productCount = (priceList.price_list_items as any)[0]?.count || 0;
        const now = new Date();
        const validFrom = new Date(priceList.valid_from);
        const validUntil = priceList.valid_until ? new Date(priceList.valid_until) : null;
        let status: 'active' | 'draft' | 'expired' = 'draft';
        if (validFrom <= now && (!validUntil || validUntil >= now)) {
          status = 'active';
        } else if (validUntil && validUntil < now) {
          status = 'expired';
        }

        // Prefer full_name from profiles table, but use email as fallback if full_name is not available
        // Extract a more readable name from email if full_name is missing
        let creatorDisplayName: string | null = null;
        if (creator?.full_name) {
          creatorDisplayName = creator.full_name;
        } else if (creator?.email) {
          // Ricava il nome dall'email: "mario.rossi@example.com" -> "Mario Rossi"
          const emailParts = creator.email.split('@')[0];
          const nameParts = emailParts.split(/[._-]/);
          creatorDisplayName = nameParts
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
        }

        return {
          ...priceList,
          customer: customer ? {
            id: customer.id,
            company_name: customer.company_name
          } as Customer : undefined,
          product_count: productCount,
          status,
          creator_name: creatorDisplayName
        };
      });

      setPriceLists(processedPriceLists);

      // Calcola i KPI in base a cosa l'utente sta filtrando o può vedere
      // Se l'utente è un commerciale, RLS ha già filtrato i listini
      // Se l'utente è admin, vogliamo che i KPI riflettano il filtro selezionato (o tutti di default)
      let kpiLists = processedPriceLists;
      if (selectedCreatorFilter === 'me') {
        kpiLists = processedPriceLists.filter(pl => pl.created_by === user.id);
      } else if (selectedCreatorFilter !== 'all') {
        kpiLists = processedPriceLists.filter(pl => pl.creator_name === selectedCreatorFilter);
      }

      const total = kpiLists.length;
      const active = kpiLists.filter(pl => pl.status === 'active').length;
      const drafts = kpiLists.filter(pl => pl.status === 'draft').length;

      // Calcola lo sconto medio sui listini usati per i KPI
      const kpiListIds = kpiLists.map(pl => pl.id);
      
      let averageDiscount = 0;
      if (kpiListIds.length > 0) {
        const { data: itemsData } = await supabase
          .from('price_list_items')
          .select('discount_percentage')
          .in('price_list_id', kpiListIds);

        let totalDiscount = 0;
        let itemCount = 0;

        if (itemsData) {
          itemsData.forEach(item => {
            totalDiscount += item.discount_percentage || 0;
            itemCount++;
          });
        }
        averageDiscount = itemCount > 0 ? Math.round(totalDiscount / itemCount) : 0;
      }

      setKpiData({
        total,
        active,
        drafts,
        averageDiscount
      });

    } catch (error) {
      console.error('Error loading price lists:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare le offerte'
      });
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [addNotification, user?.id]);

  useEffect(() => {
    loadPriceLists();
  }, [loadPriceLists, user?.id]);

  const handleNewPriceList = () => {
    setSelectedPriceListId(undefined);
    setShowDetailModal(true);
  };

  const handleEditPriceList = (priceListId: string) => {
    setSelectedPriceListId(priceListId);
    setShowDetailModal(true);
  };

  const handleViewPriceList = (priceListId: string) => {
    setPrintPriceListId(priceListId);
    setShowPrintView(true);
  };

  // MODIFICA 3: La funzione ora si chiama handleArchive... e imposta il listino da archiviare
  const handleArchivePriceList = (priceList: PriceListWithDetails) => {
    setPriceListToArchive(priceList);
  };

  // MODIFICA 4: Logica di archiviazione che fa UPDATE invece di DELETE
  const confirmPriceListArchive = async () => {
    if (!priceListToArchive) return;

    try {
      const { error } = await supabase
        .from('price_lists')
        .update({ is_active: false })
        .eq('id', priceListToArchive.id);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Offerta Archiviata',
        message: `${priceListToArchive.name} è stata archiviata`
      });

      await loadPriceLists(true);
    } catch (error: any) {
      console.error('Error archiving price list:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Impossibile archiviare l\'offerta'
      });
    } finally {
      setPriceListToArchive(null);
    }
  };

  const handleDuplicatePriceList = (priceList: PriceListWithDetails) => {
    setPriceListToDuplicate(priceList);
    setDuplicateName(`${priceList.name} - copia`);
  };

  const resetDuplicateDialog = () => {
    setPriceListToDuplicate(null);
    setDuplicateName('');
    setIsDuplicating(false);
  };

  const confirmPriceListDuplicate = async () => {
    if (!priceListToDuplicate || !duplicateName.trim()) {
      return;
    }

    setIsDuplicating(true);

    try {
      const { data: originalPriceList, error: fetchError } = await supabase
        .from('price_lists')
        .select(`
          *,
          price_list_items (*)
        `)
        .eq('id', priceListToDuplicate.id)
        .single();

      if (fetchError) throw fetchError;
      if (!originalPriceList) throw new Error('Impossibile recuperare l\'offerta originale');

      const typedOriginal = originalPriceList as PriceListWithItems;

      const { data: newPriceList, error: insertError } = await supabase
        .from('price_lists')
        .insert({
          name: duplicateName.trim(),
          description: typedOriginal.description,
          is_default: typedOriginal.is_default,
          valid_from: typedOriginal.valid_from,
          valid_until: typedOriginal.valid_until,
          currency: typedOriginal.currency,
          is_active: typedOriginal.is_active,
          print_conditions: (typedOriginal as any).print_conditions ?? true,
          payment_conditions: typedOriginal.payment_conditions,
          shipping_conditions: typedOriginal.shipping_conditions,
          delivery_conditions: typedOriginal.delivery_conditions,
          brand_conditions: typedOriginal.brand_conditions,
          created_by: user?.id || typedOriginal.created_by,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      if (!newPriceList) throw new Error('Impossibile creare la nuova offerta');

      const itemsToClone = typedOriginal.price_list_items || [];
      if (itemsToClone.length > 0) {
        const chunkSize = 100;
        for (let i = 0; i < itemsToClone.length; i += chunkSize) {
          const chunk = itemsToClone.slice(i, i + chunkSize).map((item) => ({
            price_list_id: newPriceList.id,
            product_id: item.product_id,
            price: item.price,
            discount_percentage: item.discount_percentage,
            min_quantity: item.min_quantity,
            // Righe manuali: si portano dietro codice/descrizione liberi
            ...pickCustomLineFields(item),
          }));

          const { error: itemsError } = await supabase.from('price_list_items').insert(chunk);
          if (itemsError) throw itemsError;
        }
      }

      addNotification({
        type: 'success',
        title: 'Offerta duplicata',
        message: `"${priceListToDuplicate.name}" è stata duplicata come "${duplicateName.trim()}"`,
      });

      resetDuplicateDialog();
      await loadPriceLists(true);
    } catch (error: any) {
      console.error('Error duplicating price list:', error);
      addNotification({
        type: 'error',
        title: 'Errore duplicazione',
        message: error.message || 'Impossibile duplicare l\'offerta',
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleModalClose = () => {
    setShowDetailModal(false);
    setSelectedPriceListId(undefined);
  };

  const handleModalSaveSuccess = (newPriceListId?: string) => {
    loadPriceLists(true);
    if (newPriceListId) {
      setSelectedPriceListId(newPriceListId);
    }
  };

  const handleClosePrintView = () => {
    setShowPrintView(false);
    setPrintPriceListId('');
  };

  // Generate consistent color for each creator
  const getCreatorColor = (creatorName: string | null | undefined): string => {
    if (!creatorName) return '#9CA3AF'; // gray for null/undefined

    const nameLower = creatorName.toLowerCase().trim();

    // Special cases for specific creators with fixed colors
    // Check for exact match or if name contains the keyword
    if (nameLower === 'gigi' || nameLower.startsWith('gigi ')) {
      return '#3B82F6'; // blue
    }

    // Antonio gets green (distinct from blue)
    if (nameLower.includes('antonio') ||
      nameLower.includes('antonio pasetti') ||
      (nameLower.includes('@') && nameLower.includes('antonio'))) {
      return '#10B981'; // green
    }

    // Contabilita gets yellow - check both in name and email
    if (nameLower.includes('contabilita') ||
      nameLower.includes('contabilità') ||
      nameLower.startsWith('contabilita') ||
      (nameLower.includes('@') && nameLower.includes('contabilita'))) {
      return '#EAB308'; // yellow
    }

    // Generate a hash from the name for other creators
    let hash = 0;
    for (let i = 0; i < creatorName.length; i++) {
      hash = creatorName.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use a predefined color palette with better color separation
    const colors = [
      '#EF4444', // red
      '#10B981', // green
      '#8B5CF6', // violet
      '#EC4899', // pink
      '#06B6D4', // cyan
      '#F97316', // orange
      '#6366F1', // indigo
      '#14B8A6', // teal
      '#A855F7', // purple
      '#84CC16', // lime
    ];

    return colors[Math.abs(hash) % colors.length];
  };

  // Get unique creators from price lists
  const uniqueCreators = Array.from(
    new Set(priceLists.map(pl => pl.creator_name).filter(Boolean) as string[])
  ).sort();

  const filteredPriceLists = priceLists.filter(priceList => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (priceList.name?.toLowerCase() || '').includes(searchLower) ||
      (priceList.customer?.company_name?.toLowerCase() || '').includes(searchLower) ||
      (priceList.description?.toLowerCase() || '').includes(searchLower);

    // Filtro per creatore:
    // - 'me': solo i listini dell'utente corrente (default)
    // - 'all': tutti i listini
    // - altrimenti: solo i listini del creatore selezionato
    let matchesCreator = false;
    if (selectedCreatorFilter === 'me') {
      matchesCreator = priceList.created_by === user?.id;
    } else if (selectedCreatorFilter === 'all') {
      matchesCreator = true;
    } else {
      matchesCreator = priceList.creator_name === selectedCreatorFilter;
    }

    return matchesSearch && matchesCreator;
  });

  const statusConfig = {
    active: { label: 'Attiva', badge: 'bg-green-50 text-green-700 border-green-200', accent: 'bg-green-500' },
    draft: { label: 'Bozza', badge: 'bg-amber-50 text-amber-700 border-amber-200', accent: 'bg-amber-400' },
    expired: { label: 'Scaduta', badge: 'bg-red-50 text-red-700 border-red-200', accent: 'bg-red-500' }
  } as const;

  const getCreatorInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  };

  const formatDate = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  const kpiCards = [
    { label: 'Offerte Totali', value: kpiData.total, icon: FileText, color: 'text-blue-500' },
    { label: 'Offerte Attive', value: kpiData.active, icon: TrendingUp, color: 'text-green-500' },
    { label: 'Bozze', value: kpiData.drafts, icon: Package, color: 'text-amber-500' },
    { label: 'Sconto Medio', value: `${kpiData.averageDiscount}%`, icon: Percent, color: 'text-purple-500' }
  ];

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="h-9 w-56 rounded-lg bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-20 md:h-24 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
        <Card className="h-14 animate-pulse bg-gray-50 border-gray-100" />
        <div className="grid gap-3">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">Offerte Cliente</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Gestisci le offerte personalizzate per i tuoi clienti
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
          <Button variant="outline" onClick={() => setShowBulkModal(true)} className="h-10">
            <Copy className="w-4 h-4 mr-2" />
            Offerta massiva
          </Button>
          <Button
            onClick={handleNewPriceList}
            className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Offerta
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpiCards.map((kpi) => (
          <Card key={kpi.label} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-[10px] md:text-xs font-medium text-neutral-500 uppercase tracking-wider truncate">
                  {kpi.label}
                </p>
                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4 flex-shrink-0', kpi.color)} />
              </div>
              <div className="text-xl md:text-2xl font-bold text-gray-900">{kpi.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card className="p-2">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Cerca per nome, cliente o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 md:h-11 border-none bg-transparent focus-visible:ring-0 text-base"
            />
          </div>
          <div className="sm:w-60">
            <Select value={selectedCreatorFilter} onValueChange={setSelectedCreatorFilter}>
              <SelectTrigger className="flex items-center gap-2 w-full h-10 md:h-11 bg-gray-50 border-gray-200">
                <SelectValue placeholder="Filtra per creatore" />
                {selectedCreatorFilter !== 'all' && selectedCreatorFilter !== 'me' && (
                  <div
                    className="w-3 h-3 rounded-full ml-2 flex-shrink-0"
                    style={{ backgroundColor: getCreatorColor(selectedCreatorFilter) }}
                  />
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Le mie offerte</SelectItem>
                {(profile?.role === 'admin' || profile?.role === 'amministrazione') && (
                  <SelectItem value="all">Tutti i creatori</SelectItem>
                )}
                {(profile?.role === 'admin' || profile?.role === 'amministrazione') && uniqueCreators.map((creator) => (
                  <SelectItem key={creator} value={creator}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getCreatorColor(creator) }}
                      />
                      <span>{creator}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Price Lists List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
            {filteredPriceLists.length} {filteredPriceLists.length === 1 ? 'offerta' : 'offerte'}
          </p>
        </div>

        {filteredPriceLists.length === 0 ? (
          <div className="text-center py-14 rounded-xl border border-dashed border-gray-200 bg-white">
            <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-4 opacity-30" />
            <p className="text-neutral-500">
              {searchTerm ? 'Nessuna offerta trovata per la ricerca' : 'Nessuna offerta creata'}
            </p>
            {!searchTerm && (
              <Button className="mt-4" onClick={handleNewPriceList}>
                <Plus className="w-4 h-4 mr-2" />
                Crea la prima offerta
              </Button>
            )}
          </div>
        ) : (
          filteredPriceLists.map((priceList) => {
            const config = statusConfig[priceList.status];
            const validFrom = formatDate(priceList.valid_from);
            const validUntil = formatDate(priceList.valid_until);

            return (
              <Card
                key={priceList.id}
                onClick={() => handleEditPriceList(priceList.id)}
                className="group relative overflow-hidden p-4 cursor-pointer bg-white border-gray-200 hover:border-primary-500 hover:shadow-xl transition-all duration-300"
              >
                <div className={cn('absolute left-0 top-0 h-full w-1 opacity-70 group-hover:opacity-100 transition-opacity', config.accent)} />

                <div className="flex flex-col gap-3 pl-2 md:flex-row md:items-center md:justify-between md:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-base md:text-lg text-gray-900 group-hover:text-primary-600 transition-colors break-words">
                        {priceList.name}
                      </h3>
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border', config.badge)}>
                        {config.label}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs md:text-sm text-neutral-500">
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                        <span className="truncate max-w-[220px]">
                          {priceList.customer?.company_name || 'Nessun cliente'}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                        {validFrom || '-'}{validUntil ? ` → ${validUntil}` : ''}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                        {priceList.product_count} {priceList.product_count === 1 ? 'prodotto' : 'prodotti'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3 md:border-t-0 md:pt-0 md:justify-end">
                    {priceList.creator_name ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: getCreatorColor(priceList.creator_name) }}
                          title={priceList.creator_name}
                        >
                          {getCreatorInitials(priceList.creator_name)}
                        </div>
                        <span className="text-xs text-neutral-500 truncate max-w-[130px]">
                          {priceList.creator_name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">Creatore sconosciuto</span>
                    )}

                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Anteprima stampa"
                        className="h-8 w-8 rounded-full text-teal-600 hover:bg-teal-50"
                        onClick={() => handleViewPriceList(priceList.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-neutral-500 hover:bg-gray-100">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewPriceList(priceList.id)}><Eye className="w-4 h-4 mr-2" /> Visualizza</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditPriceList(priceList.id)}><Edit className="w-4 h-4 mr-2" /> Modifica</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicatePriceList(priceList)}>
                            <Copy className="w-4 h-4 mr-2" /> Duplica
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleArchivePriceList(priceList)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Archivia
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Price List Detail Modal */}
      <PriceListDetailPage
        isOpen={showDetailModal}
        onClose={handleModalClose}
        priceListId={selectedPriceListId}
        onSaveSuccess={handleModalSaveSuccess}
      />

      {/* Price List Print View */}
      <PriceListPrintView
        isOpen={showPrintView}
        onClose={handleClosePrintView}
        priceListId={printPriceListId}
      />

      {/* Bulk Price List Modal */}
      <BulkPriceListModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onCreated={() => {
          setShowBulkModal(false);
          loadPriceLists();
        }}
      />

      {/* MODIFICA: Dialogo di conferma ARCHIVIAZIONE */}
      <Dialog open={!!priceListToArchive} onOpenChange={() => setPriceListToArchive(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <span>Conferma Archiviazione</span>
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler archiviare l'offerta "{priceListToArchive?.name}"? Non sarà più utilizzabile per nuovi ordini.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceListToArchive(null)}>Annulla</Button>
            <Button variant="destructive" onClick={confirmPriceListArchive}>Sì, archivia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!priceListToDuplicate} onOpenChange={(open) => {
        if (!open && !isDuplicating) {
          resetDuplicateDialog();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplica Offerta</DialogTitle>
            <DialogDescription>
              Inserisci il nuovo nome per duplicare "{priceListToDuplicate?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              placeholder="Nome nuova offerta"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDuplicateDialog} disabled={isDuplicating}>
              Annulla
            </Button>
            <Button onClick={confirmPriceListDuplicate} disabled={!duplicateName.trim() || isDuplicating}>
              {isDuplicating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Duplica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PriceListsPage;